import "@fontsource-variable/bricolage-grotesque/index.css";
import "@fontsource-variable/atkinson-hyperlegible-next/index.css";
import "@fontsource/ibm-plex-mono/500.css";
import "./styles/tokens.css";
import "./styles/base.css";
import "./styles/components.css";
import "./styles/shell.css";
import "./styles/auth.css";
import "./styles/recipes.css";
import "./styles/cook.css";
import "./styles/groceries.css";
import "./styles/pantry.css";
import "./styles/plan.css";
import "./styles/settings.css";
import "./styles/print.css";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router";
import { ApiError, OfflineError } from "./lib/api";
import { connectivity } from "./lib/connectivity";
import { installHouseholdSync, isHouseholdRead } from "./lib/householdSync";
import { router } from "./router";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Always attempt the request: the API layer falls back to the private offline cache.
      networkMode: "always",
      staleTime: 20_000,
      gcTime: 10 * 60_000,
      // Shared household reads use the visible-page scheduler, which also guards direct writes.
      refetchOnWindowFocus: (query) => !isHouseholdRead(query.queryKey),
      refetchOnReconnect: (query) => !isHouseholdRead(query.queryKey),
      retry: (count, error) => {
        if (error instanceof ApiError || error instanceof OfflineError) return false;
        return count < 2;
      },
    },
    mutations: { networkMode: "always", retry: false },
  },
});

// On reconnect, revalidate authoritative data before anything claims to be current.
connectivity.onReconnect(() => void queryClient.invalidateQueries({ predicate: (query) => !isHouseholdRead(query.queryKey) }));
const stopHouseholdSync = installHouseholdSync(queryClient);
if (import.meta.hot) import.meta.hot.dispose(stopHouseholdSync);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
